// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  BulkItemFilterCallback,
  BulkItemSearch,
} from '@/items/components/bulk-item-search';
import { SparseItem } from '@/sparse/api/types';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If provided, this function will be used to filter the item options.
   */
  filter?: BulkItemFilterCallback;

  /**
   * If provided, these items will be hidden from the dropdown.
   */
  hiddenItems?: SparseItem[];

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholderText?: string;
}

/**
 * A controlled bulk item search component that integrates with react-hook-form.
 * Used for selecting bulk items in inventory dialogs.
 */
export function FormBulkItemSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  const value = field.value ?? null;

  return (
    <BulkItemSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      filter={props.filter}
      disabled={props.disabled}
      onChange={field.onChange}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      hiddenItems={props.hiddenItems}
      placeholderText={props.placeholderText}
    />
  );
}
